import React, {useState} from 'react'
import {Popover, PopoverTrigger, PopoverContent} from '../common/Popover'

// ============================================================
// Shared trigger button style
// ============================================================
const TriggerBtn = React.forwardRef(({children, style = {}, ...props}, ref) => (
    <button
        ref={ref}
        style={{
            padding: '6px 14px',
            fontSize: 13,
            border: '1px solid #dee2e6',
            borderRadius: 4,
            background: '#fff',
            cursor: 'pointer',
            color: '#303238',
            ...style,
        }}
        {...props}
    >
        {children}
    </button>
))

// ============================================================
// Figma Style 1 — Context Menu Content
// ============================================================
const ContextMenuContent = ({onClose}) => {
    const items = [
        {label: 'Hand tool', shortcut: 'H / Hold Spacebar'},
        {label: 'Move tool', shortcut: '↑ ↓ ← →'},
        {label: 'Delete', shortcut: 'Delete'},
        null, // divider
        {label: 'Copy', shortcut: 'Ctrl + C'},
        {label: 'Cut', shortcut: 'Ctrl + X'},
        {label: 'Paste', shortcut: 'Ctrl + V'},
        null, // divider
        {label: 'Undo', shortcut: 'Ctrl + Z'},
        {label: 'Redo', shortcut: 'Ctrl + Y'},
    ]

    return (
        <div className="popover-context-menu">
            {items.map((item, i) =>
                item === null ? (
                    <div key={i} className="popover-context-menu__divider" />
                ) : (
                    <div
                        key={i}
                        className="popover-context-menu__item"
                        onClick={() => onClose?.()}
                    >
                        <span className="popover-context-menu__label">{item.label}</span>
                        <span className="popover-context-menu__shortcut">{item.shortcut}</span>
                    </div>
                )
            )}
        </div>
    )
}

// ============================================================
// Figma Style 2 — Rich Content (Tutorial)
// ============================================================
const RichContentDemo = ({onClose}) => {
    const items = [
        {
            title: 'Breed Area',
            bullets: [
                <>Your design must <strong>fully cover</strong> this area.</>,
                <>This ensures that the artwork <em>won't be cut off</em> during production and will adapt correctly across <em>different sizes</em>.</>,
            ],
        },
        {
            title: 'Safe Zone',
            bullets: [
                <>The <strong>main elements</strong> of your design should be <strong>placed</strong> within this area.</>,
                <>All important details, logos, and artwork inside the Safe Zone <em>will always be visible</em> across all sizes.</>,
            ],
        },
        {
            title: '3D Preview Area',
            bullets: [
                <>The product's <strong>3D preview</strong> will be <strong>displayed</strong> based on this outline.</>,
                <>This area helps you visualize how your design will <em>appear on the actual product</em>.</>,
            ],
        },
    ]

    return (
        <div className="popover-rich" style={{width: 360}}>
            <div className="popover-rich__header">
                <p className="popover-rich__title">Design tutorial guideline</p>
                <button className="popover-rich__close" onClick={onClose}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                </button>
            </div>
            <div className="popover-rich__body">
                {items.map((item, i) => (
                    <div key={i} className="popover-rich__item">
                        {/* Placeholder icon box */}
                        <div
                            className="popover-rich__icon"
                            style={{
                                background: '#f3f4f6',
                                borderRadius: 4,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 10,
                                color: '#989b9f',
                                flexShrink: 0,
                            }}
                        >
                            Icon
                        </div>
                        <div className="popover-rich__text">
                            <p className="popover-rich__text-title">{item.title}</p>
                            {item.bullets.map((b, j) => (
                                <ul key={j} className="popover-rich__text-body">
                                    <li>{b}</li>
                                </ul>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

// ============================================================
// Figma Style 3 — Form Panel (Offset path)
// ============================================================
const FormPanelDemo = ({onClose}) => {
    const [value, setValue] = useState(12)

    return (
        <div className="popover-form" style={{width: 220}}>
            <p className="popover-form__title">Offset path</p>

            <div className="popover-form__field">
                <div className="popover-form__field-row">
                    <span className="popover-form__label">Offset size</span>
                    <span className="popover-form__input">{value} px</span>
                </div>

                <div className="popover-form__slider" style={{position: 'relative', height: 16}}>
                    <div className="popover-form__slider-track" />
                    <div
                        className="popover-form__slider-fill"
                        style={{width: `${(value / 50) * 100}%`}}
                    />
                    <input
                        type="range"
                        min={0}
                        max={50}
                        value={value}
                        onChange={(e) => setValue(Number(e.target.value))}
                        className="popover-form__slider-thumb"
                        style={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            top: 0,
                            left: 0,
                            opacity: 0,
                            cursor: 'pointer',
                            margin: 0,
                        }}
                    />
                    {/* Visual thumb */}
                    <div
                        style={{
                            position: 'absolute',
                            left: `calc(${(value / 50) * 100}% - 8px)`,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: 16,
                            height: 16,
                            borderRadius: '50%',
                            background: '#fff',
                            border: '2px solid #007bff',
                            boxShadow: '0 1px 2px rgba(15,23,42,0.05)',
                            pointerEvents: 'none',
                        }}
                    />
                </div>
            </div>

            <div className="popover-form__actions">
                <button className="popover-form__btn popover-form__btn--cancel" onClick={onClose}>
                    Cancel
                </button>
                <button className="popover-form__btn popover-form__btn--apply" onClick={onClose}>
                    Apply
                </button>
            </div>
        </div>
    )
}

// ============================================================
// Placement Grid
// ============================================================
const PLACEMENTS = [
    'top-start', 'top', 'top-end',
    'left-start', null, 'right-start',
    'left', null, 'right',
    'left-end', null, 'right-end',
    'bottom-start', 'bottom', 'bottom-end',
]

const PlacementGrid = () => (
    <div
        style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 120px)',
            gridTemplateRows: 'repeat(5, 36px)',
            gap: 6,
            alignItems: 'center',
            justifyItems: 'center',
        }}
    >
        {PLACEMENTS.map((p, i) =>
            p === null ? (
                <div key={i} />
            ) : (
                <Popover key={p} placement={p} trigger="click" showArrow>
                    <PopoverTrigger>
                        <TriggerBtn style={{fontSize: 11, padding: '4px 8px', width: '100%'}}>
                            {p}
                        </TriggerBtn>
                    </PopoverTrigger>
                    <PopoverContent>
                        <div style={{padding: '8px 12px', fontSize: 13, color: '#303238', whiteSpace: 'nowrap'}}>
                            Placement: <strong>{p}</strong>
                        </div>
                    </PopoverContent>
                </Popover>
            )
        )}
    </div>
)

// ============================================================
// Controlled Demo
// ============================================================
const ControlledDemo = () => {
    const [open, setOpen] = useState(false)
    return (
        <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
            <Popover isOpen={open} onOpenChange={setOpen} placement="bottom">
                <PopoverTrigger>
                    <TriggerBtn>Controlled Trigger</TriggerBtn>
                </PopoverTrigger>
                <PopoverContent>
                    <div style={{padding: '10px 14px', fontSize: 13}}>
                        Controlled popover — isOpen: <strong>{String(open)}</strong>
                    </div>
                </PopoverContent>
            </Popover>
            <button
                style={{fontSize: 13, padding: '6px 12px', cursor: 'pointer', borderRadius: 4, border: '1px solid #dee2e6'}}
                onClick={() => setOpen((v) => !v)}
            >
                Toggle from outside
            </button>
        </div>
    )
}

// ============================================================
// Main Playground Page
// ============================================================
const Section = ({title, children}) => (
    <section style={{marginBottom: 48}}>
        <h2 style={{fontSize: 16, fontWeight: 600, color: '#303238', marginBottom: 20, paddingBottom: 10, borderBottom: '1px solid #dee2e6'}}>
            {title}
        </h2>
        {children}
    </section>
)

const Row = ({label, children}) => (
    <div style={{display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16, flexWrap: 'wrap'}}>
        {label && (
            <span style={{fontSize: 12, color: '#989b9f', minWidth: 100, paddingTop: 8}}>{label}</span>
        )}
        <div style={{display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center'}}>
            {children}
        </div>
    </div>
)

export default function PlaygroundPage() {
    return (
        <div style={{
            padding: '40px 60px',
            maxWidth: 900,
            margin: '0 auto',
            fontFamily: "Roboto, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
            color: '#303238',
        }}>
            <h1 style={{fontSize: 24, fontWeight: 600, marginBottom: 8}}>Popover Playground</h1>
            <p style={{color: '#65676b', marginBottom: 40, fontSize: 14}}>
                Reusable Popover component built with @floating-ui/react. API inspired by HeroUI.
            </p>

            {/* ── Section 1: Figma Styles ── */}
            <Section title="Figma Styles">
                <Row label="Context Menu">
                    <Popover placement="bottom-start" trigger="click" radius="sm" shadow="md">
                        <PopoverTrigger>
                            <TriggerBtn>Right-click Menu</TriggerBtn>
                        </PopoverTrigger>
                        <PopoverContent>
                            {(close) => <ContextMenuContent onClose={close} />}
                        </PopoverContent>
                    </Popover>
                </Row>

                <Row label="Rich Content">
                    <Popover placement="bottom" trigger="click" radius="sm" shadow="lg" size="lg">
                        <PopoverTrigger>
                            <TriggerBtn>Tutorial Guide</TriggerBtn>
                        </PopoverTrigger>
                        <PopoverContent>
                            {(close) => <RichContentDemo onClose={close} />}
                        </PopoverContent>
                    </Popover>
                </Row>

                <Row label="Form Panel">
                    <Popover placement="right" trigger="click" radius="lg" shadow="md">
                        <PopoverTrigger>
                            <TriggerBtn>Offset Path</TriggerBtn>
                        </PopoverTrigger>
                        <PopoverContent>
                            {(close) => <FormPanelDemo onClose={close} />}
                        </PopoverContent>
                    </Popover>
                </Row>
            </Section>

            {/* ── Section 2: Trigger Types ── */}
            <Section title="Trigger Types">
                <Row label="click">
                    <Popover trigger="click" placement="bottom">
                        <PopoverTrigger>
                            <TriggerBtn>Click me</TriggerBtn>
                        </PopoverTrigger>
                        <PopoverContent>
                            <div style={{padding: '8px 12px', fontSize: 13}}>Opened by click</div>
                        </PopoverContent>
                    </Popover>
                </Row>
                <Row label="hover">
                    <Popover trigger="hover" placement="top">
                        <PopoverTrigger>
                            <TriggerBtn>Hover me</TriggerBtn>
                        </PopoverTrigger>
                        <PopoverContent>
                            <div style={{padding: '8px 12px', fontSize: 13}}>Opened by hover</div>
                        </PopoverContent>
                    </Popover>
                </Row>
                <Row label="focus">
                    <Popover trigger="focus" placement="bottom">
                        <PopoverTrigger>
                            <input
                                style={{padding: '6px 10px', border: '1px solid #dee2e6', borderRadius: 4, fontSize: 13}}
                                placeholder="Focus me (Tab in)"
                                readOnly
                            />
                        </PopoverTrigger>
                        <PopoverContent>
                            <div style={{padding: '8px 12px', fontSize: 13}}>Opened by focus</div>
                        </PopoverContent>
                    </Popover>
                </Row>
                <Row label="click+hover">
                    <Popover trigger={['click', 'hover']} placement="bottom">
                        <PopoverTrigger>
                            <TriggerBtn>Click or Hover</TriggerBtn>
                        </PopoverTrigger>
                        <PopoverContent>
                            <div style={{padding: '8px 12px', fontSize: 13}}>Click or hover trigger</div>
                        </PopoverContent>
                    </Popover>
                </Row>
            </Section>

            {/* ── Section 3: Arrow ── */}
            <Section title="Arrow (showArrow)">
                <Row label="">
                    {['top', 'bottom', 'left', 'right'].map((p) => (
                        <Popover key={p} placement={p} trigger="click" showArrow>
                            <PopoverTrigger>
                                <TriggerBtn>{p}</TriggerBtn>
                            </PopoverTrigger>
                            <PopoverContent>
                                <div style={{padding: '8px 12px', fontSize: 13, whiteSpace: 'nowrap'}}>
                                    Arrow → <strong>{p}</strong>
                                </div>
                            </PopoverContent>
                        </Popover>
                    ))}
                </Row>
            </Section>

            {/* ── Section 4: Sizes ── */}
            <Section title="Sizes">
                <Row label="">
                    {['sm', 'md', 'lg'].map((s) => (
                        <Popover key={s} placement="bottom" trigger="click" size={s}>
                            <PopoverTrigger>
                                <TriggerBtn>size="{s}"</TriggerBtn>
                            </PopoverTrigger>
                            <PopoverContent>
                                <div style={{padding: '12px 16px', fontSize: 13}}>
                                    Size: <strong>{s}</strong>
                                </div>
                            </PopoverContent>
                        </Popover>
                    ))}
                </Row>
            </Section>

            {/* ── Section 5: Shadow ── */}
            <Section title="Shadows">
                <Row label="">
                    {['sm', 'md', 'lg'].map((s) => (
                        <Popover key={s} placement="bottom" trigger="click" shadow={s}>
                            <PopoverTrigger>
                                <TriggerBtn>shadow="{s}"</TriggerBtn>
                            </PopoverTrigger>
                            <PopoverContent>
                                <div style={{padding: '12px 16px', fontSize: 13}}>
                                    Shadow: <strong>{s}</strong>
                                </div>
                            </PopoverContent>
                        </Popover>
                    ))}
                </Row>
            </Section>

            {/* ── Section 6: Backdrop ── */}
            <Section title="Backdrop">
                <Row label="">
                    {['transparent', 'opaque', 'blur'].map((b) => (
                        <Popover key={b} placement="bottom" trigger="click" backdrop={b}>
                            <PopoverTrigger>
                                <TriggerBtn>backdrop="{b}"</TriggerBtn>
                            </PopoverTrigger>
                            <PopoverContent>
                                <div style={{padding: '12px 16px', fontSize: 13}}>
                                    Backdrop: <strong>{b}</strong>
                                </div>
                            </PopoverContent>
                        </Popover>
                    ))}
                </Row>
            </Section>

            {/* ── Section 7: Controlled ── */}
            <Section title="Controlled Mode">
                <ControlledDemo />
            </Section>

            {/* ── Section 8: Placements ── */}
            <Section title="All 12 Placements">
                <div style={{display: 'flex', justifyContent: 'center', padding: '20px 0'}}>
                    <PlacementGrid />
                </div>
            </Section>
        </div>
    )
}
